import React from 'react';
import './privacy-policy.css';

export default function PrivacyPolicyData() {
    const lastUpdated = "August 4, 2026";

    const sections = [
        {
            id: "introduction",
            title: "Introduction",
            content: (
                <>
                    <p>
                        Welcome to <strong>Spinnennetz</strong> ("we," "our," or "us"). We are committed to protecting your privacy and handling your personal data with transparency and integrity. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website at <a href="https://www.thespinnennetz.com">www.thespinnennetz.com</a> or use any of our services.
                    </p>
                    <p>
                        Please read this Privacy Policy carefully. By accessing or using our website and services, you acknowledge that you have read, understood, and agree to the terms described here. If you do not agree with any part of this policy, please discontinue use of our site.
                    </p>
                </>
            ),
        },
        {
            id: "information-we-collect",
            title: "Information We Collect",
            content: (
                <>
                    <p>We may collect the following categories of information:</p>
                    <div className="pp-subsection">
                        <h4>1. Personal Information You Provide</h4>
                        <p>When you fill out our contact forms, request a consultation, or interact with us, we collect information such as:</p>
                        <ul className="pp-list">
                            <li>Full name</li>
                            <li>Email address</li>
                            <li>Company or business name</li>
                            <li>Website URL</li>
                            <li>Any other information you voluntarily provide in forms or messages</li>
                        </ul>
                    </div>
                    <div className="pp-subsection">
                        <h4>2. Automatically Collected Information</h4>
                        <p>When you visit our website, we automatically collect certain technical data, including:</p>
                        <ul className="pp-list">
                            <li>IP address</li>
                            <li>Browser type and version</li>
                            <li>Operating system</li>
                            <li>Pages visited and time spent on each page</li>
                            <li>Referring website or source</li>
                            <li>Device type (desktop, tablet, mobile)</li>
                            <li>Clickstream data and user behavior on our site</li>
                        </ul>
                    </div>
                    <div className="pp-subsection">
                        <h4>3. Cookies and Tracking Technologies</h4>
                        <p>We use cookies, pixel tags, and similar technologies to collect data that helps us understand how visitors use our website. You can manage cookie preferences through your browser settings at any time.</p>
                    </div>
                </>
            ),
        },
        {
            id: "how-we-use",
            title: "How We Use Your Information",
            content: (
                <>
                    <p>We use the information we collect for the following purposes:</p>
                    <ul className="pp-list pp-check-list">
                        <li>To respond to your inquiries, consultation requests, and messages</li>
                        <li>To deliver and improve our CRO, web development, and digital marketing services</li>
                        <li>To send you relevant service updates, case studies, or insights (only with your consent)</li>
                        <li>To analyze website traffic and user behavior for performance optimization</li>
                        <li>To run A/B tests and CRO experiments on our own site</li>
                        <li>To comply with legal obligations and resolve any disputes</li>
                        <li>To prevent fraud, spam, and other misuse of our services</li>
                        <li>To personalize your experience on our website</li>
                    </ul>
                </>
            ),
        },
        {
            id: "sharing-information",
            title: "Sharing of Information",
            content: (
                <>
                    <p>We do <strong>not</strong> sell, trade, or rent your personal information to third parties. However, we may share your data in the following limited circumstances:</p>
                    <div className="pp-subsection">
                        <h4>Service Providers</h4>
                        <p>We may share your data with trusted third-party vendors who assist us in operating our website or delivering services — such as email platforms, analytics tools, CRM software, and cloud hosting providers. These parties are bound by data processing agreements and may only use your information for the purpose of providing services on our behalf.</p>
                    </div>
                    <div className="pp-subsection">
                        <h4>Business Transfers</h4>
                        <p>In the event of a merger, acquisition, or sale of assets, your information may be transferred as part of that transaction. We will notify you via email or prominent notice on our website if such a change occurs.</p>
                    </div>
                    <div className="pp-subsection">
                        <h4>Legal Requirements</h4>
                        <p>We may disclose your information if required by law, or in response to valid legal processes such as a court order, subpoena, or government regulation.</p>
                    </div>
                    <div className="pp-subsection">
                        <h4>Protection of Rights</h4>
                        <p>We may share information when we believe it is necessary to investigate, prevent, or act regarding illegal activities, suspected fraud, or threats to the safety of any person.</p>
                    </div>
                </>
            ),
        },
        {
            id: "data-retention",
            title: "Data Retention",
            content: (
                <>
                    <p>
                        We retain your personal data only for as long as is necessary to fulfill the purposes outlined in this Privacy Policy, or as required by applicable law. Once the retention period expires, your data is securely deleted or anonymized.
                    </p>
                    <p>
                        If you would like us to delete your data sooner, please contact us using the information provided at the end of this policy.
                    </p>
                </>
            ),
        },
        {
            id: "cookies",
            title: "Cookies & Analytics",
            content: (
                <>
                    <p>Our website uses cookies and analytics tools to improve functionality and understand how users interact with our content.</p>
                    <div className="pp-subsection">
                        <h4>Types of Cookies We Use</h4>
                        <ul className="pp-list">
                            <li><strong>Essential Cookies:</strong> Necessary for the website to function properly.</li>
                            <li><strong>Analytics Cookies:</strong> Help us understand visitor behavior (e.g., Google Analytics, GA4).</li>
                            <li><strong>Marketing Cookies:</strong> Used to track visitors across websites for retargeting and advertising purposes.</li>
                            <li><strong>Preference Cookies:</strong> Remember your settings and preferences for a better experience.</li>
                        </ul>
                    </div>
                    <p>
                        You can opt out of analytics tracking by adjusting your browser settings or by using opt-out tools provided by services like Google Analytics. Note that disabling certain cookies may affect website functionality.
                    </p>
                </>
            ),
        },
        {
            id: "third-party",
            title: "Third-Party Links & Services",
            content: (
                <>
                    <p>
                        Our website may contain links to third-party websites, integrations, or platforms (e.g., LinkedIn, Facebook, Google). We are not responsible for the privacy practices or content of those external sites. We encourage you to review their respective privacy policies before providing any personal information.
                    </p>
                    <p>
                        We may use third-party tools for purposes such as CRO experimentation, heatmaps, session recording, email communication, and live chat. Each of these tools has its own privacy terms.
                    </p>
                </>
            ),
        },
        {
            id: "data-security",
            title: "Data Security",
            content: (
                <>
                    <p>
                        We implement industry-standard security measures to protect your personal information from unauthorized access, loss, or disclosure. These measures include:
                    </p>
                    <ul className="pp-list pp-check-list">
                        <li>SSL/TLS encryption for data in transit</li>
                        <li>Access controls and authentication protocols</li>
                        <li>Regular security audits and vulnerability assessments</li>
                        <li>Secure cloud infrastructure for data storage</li>
                    </ul>
                    <p>
                        While we take every reasonable precaution, no method of transmission over the Internet or electronic storage is 100% secure. We cannot guarantee absolute security of your data.
                    </p>
                </>
            ),
        },
        {
            id: "your-rights",
            title: "Your Rights",
            content: (
                <>
                    <p>Depending on your location, you may have certain rights regarding your personal data, including:</p>
                    <ul className="pp-list pp-check-list">
                        <li><strong>Right to Access:</strong> Request a copy of the personal data we hold about you.</li>
                        <li><strong>Right to Rectification:</strong> Request corrections to inaccurate or incomplete data.</li>
                        <li><strong>Right to Erasure:</strong> Request deletion of your personal data ("right to be forgotten").</li>
                        <li><strong>Right to Object:</strong> Object to the processing of your personal data for specific purposes.</li>
                        <li><strong>Right to Restrict Processing:</strong> Request that we limit how we use your data.</li>
                        <li><strong>Right to Data Portability:</strong> Receive your data in a structured, machine-readable format.</li>
                        <li><strong>Right to Withdraw Consent:</strong> Withdraw consent at any time where processing is based on consent.</li>
                    </ul>
                    <p>
                        To exercise any of these rights, please contact us at <a href="mailto:info@thespinnennetz.com">info@thespinnennetz.com</a>. We will respond to your request within a reasonable timeframe, subject to verification.
                    </p>
                </>
            ),
        },
        {
            id: "children",
            title: "Children's Privacy",
            content: (
                <>
                    <p>
                        Our website and services are not directed at children under the age of 13. We do not knowingly collect personal information from children. If we become aware that we have collected personal data from a child without verifiable parental consent, we will take steps to delete that information promptly.
                    </p>
                    <p>
                        If you are a parent or guardian and believe your child has provided us with personal information, please contact us immediately at <a href="mailto:info@thespinnennetz.com">info@thespinnennetz.com</a>.
                    </p>
                </>
            ),
        },
        {
            id: "changes",
            title: "Changes to This Privacy Policy",
            content: (
                <>
                    <p>
                        We may update this Privacy Policy from time to time to reflect changes in our practices, technology, or legal requirements. When we make significant changes, we will update the "Last Updated" date at the top of this page and, where appropriate, notify you via email.
                    </p>
                    <p>
                        We encourage you to review this Privacy Policy periodically to stay informed about how we are protecting your information.
                    </p>
                </>
            ),
        },
        {
            id: "contact",
            title: "Contact Us",
            content: (
                <>
                    <p>
                        If you have any questions, concerns, or requests regarding this Privacy Policy or the way we handle your personal data, please reach out to us:
                    </p>
                    <div className="pp-contact-info">
                        <div className="pp-contact-item">
                            <span className="pp-contact-icon">
                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none">
                                    <path d="M9.366 10.682C10.3043 12.3305 11.6695 13.6957 13.318 14.634L14.202 13.396C14.3442 13.1969 14.5543 13.0569 14.7928 13.0023C15.0313 12.9478 15.2814 12.9825 15.496 13.1C16.9103 13.8729 18.4722 14.3378 20.079 14.464C20.3298 14.4839 20.5638 14.5975 20.7345 14.7823C20.9052 14.9671 21 15.2094 21 15.461V19.923C21.0001 20.1706 20.9083 20.4094 20.7424 20.5932C20.5765 20.777 20.3483 20.8927 20.102 20.918C19.572 20.973 19.038 21 18.5 21C9.94 21 3 14.06 3 5.5C3 4.962 3.027 4.428 3.082 3.898C3.10725 3.6517 3.22298 3.42352 3.40679 3.25763C3.5906 3.09175 3.82941 2.99995 4.077 3H8.539C8.79056 2.99997 9.0329 3.09475 9.21768 3.26545C9.40247 3.43615 9.51613 3.67022 9.536 3.921C9.66222 5.52779 10.1271 7.08968 10.9 8.504C11.0175 8.71856 11.0522 8.96874 10.9977 9.2072C10.9431 9.44565 10.8031 9.65584 10.604 9.798L9.366 10.682ZM6.844 10.025L8.744 8.668C8.20478 7.50409 7.83535 6.26884 7.647 5H5.01C5.004 5.166 5.001 5.333 5.001 5.5C5 12.956 11.044 19 18.5 19C18.667 19 18.834 18.997 19 18.99V16.353C17.7312 16.1646 16.4959 15.7952 15.332 15.256L13.975 17.156C13.4287 16.9437 12.898 16.6931 12.387 16.406L12.329 16.373C10.3676 15.2567 8.74328 13.6324 7.627 11.671L7.594 11.613C7.30691 11.102 7.05628 10.5713 6.844 10.025Z" fill="#57007B" />
                                </svg>
                            </span>
                            <div>
                                <span className="pp-contact-label">Phone</span>
                                <a href="tel:+919624029330">+91 9624029330</a>
                            </div>
                        </div>
                        <div className="pp-contact-item">
                            <span className="pp-contact-icon">
                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none">
                                    <path d="M3 3H21C21.2652 3 21.5196 3.10536 21.7071 3.29289C21.8946 3.48043 22 3.73478 22 4V20C22 20.2652 21.8946 20.5196 21.7071 20.7071C21.5196 20.8946 21.2652 21 21 21H3C2.73478 21 2.48043 20.8946 2.29289 20.7071C2.10536 20.5196 2 20.2652 2 20V4C2 3.73478 2.10536 3.48043 2.29289 3.29289C2.48043 3.10536 2.73478 3 3 3ZM20 7.238L12.072 14.338L4 7.216V19H20V7.238ZM4.511 5L12.061 11.662L19.502 5H4.511Z" fill="#57007B" />
                                </svg>
                            </span>
                            <div>
                                <span className="pp-contact-label">Email</span>
                                <a href="mailto:info@thespinnennetz.com">info@thespinnennetz.com</a>
                            </div>
                        </div>
                        <div className="pp-contact-item">
                            <span className="pp-contact-icon">
                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none">
                                    <path d="M12 22C6.477 22 2 17.523 2 12C2 6.477 6.477 2 12 2C17.523 2 22 6.477 22 12C22 17.523 17.523 22 12 22ZM9.71 19.667C8.72341 17.5743 8.15187 15.3102 8.027 13H4.062C4.25659 14.5389 4.89392 15.9882 5.89657 17.1717C6.89922 18.3552 8.22401 19.2221 9.71 19.667ZM10.03 13C10.181 15.439 10.878 17.73 12 19.752C13.1523 17.6766 13.8254 15.3695 13.97 13H10.03ZM19.938 13H15.973C15.8481 15.3102 15.2766 17.5743 14.29 19.667C15.776 19.2221 17.1008 18.3552 18.1034 17.1717C19.1061 15.9882 19.7434 14.5389 19.938 13ZM4.062 11H8.027C8.15187 8.68979 8.72341 6.42569 9.71 4.333C8.22401 4.77788 6.89922 5.64475 5.89657 6.8283C4.89392 8.01184 4.25659 9.4611 4.062 11ZM10.031 11H13.969C13.8248 8.6306 13.152 6.32353 12 4.248C10.8477 6.32345 10.1746 8.63052 10.03 11H10.031ZM14.29 4.333C15.2766 6.42569 15.8481 8.68979 15.973 11H19.938C19.7434 9.4611 19.1061 8.01184 18.1034 6.8283C17.1008 5.64475 15.776 4.77788 14.29 4.333Z" fill="#57007B" />
                                </svg>
                            </span>
                            <div>
                                <span className="pp-contact-label">Website</span>
                                <a href="https://www.thespinnennetz.com" target="_blank" rel="noreferrer">www.thespinnennetz.com</a>
                            </div>
                        </div>
                    </div>
                </>
            ),
        },
    ];

    return (
        <section className="pp-page cSection">
            <div className="cContainer">
                {/* Header */}
                <div className="pp-hero">
                    <div className="pp-hero-badge">Legal</div>
                    <h1 className="pp-hero-title">Privacy Policy</h1>
                    <p className="pp-hero-subtitle">
                        Your privacy matters to us. Learn how Spinnennetz collects, uses, and protects your information.
                    </p>
                    <div className="pp-last-updated">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none">
                            <path d="M12 22C6.477 22 2 17.523 2 12C2 6.477 6.477 2 12 2C17.523 2 22 6.477 22 12C22 17.523 17.523 22 12 22ZM13 12V7H11V14H17V12H13Z" fill="currentColor" />
                        </svg>
                        Last updated: {lastUpdated}
                    </div>
                </div>

                {/* Layout */}
                <div className="pp-layout">
                    {/* Sidebar TOC */}
                    <aside className="pp-sidebar">
                        <div className="pp-toc">
                            <h3 className="pp-toc-title">Table of Contents</h3>
                            <ul className="pp-toc-list">
                                {sections.map((section, idx) => (
                                    <li key={section.id}>
                                        <a href={`#${section.id}`} className="pp-toc-link">
                                            <span className="pp-toc-num">{String(idx + 1).padStart(2, '0')}</span>
                                            {section.title}
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </aside>

                    {/* Content */}
                    <main className="pp-content">
                        {sections.map((section, idx) => (
                            <div className="pp-section" id={section.id} key={section.id}>
                                <div className="pp-section-header">
                                    <span className="pp-section-num">{String(idx + 1).padStart(2, '0')}</span>
                                    <h2 className="pp-section-title">{section.title}</h2>
                                </div>
                                <div className="pp-section-body">
                                    {section.content}
                                </div>
                            </div>
                        ))}
                    </main>
                </div>
            </div>
        </section>
    );
}
